// src/screens/FannieMaeFetcher.tsx
// Redfin-style layout with 4 data-backed tabs (joined via source='fanniemae' on ScrapedDeal).
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Alert,
  LinearProgress,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import RefreshIcon from '@mui/icons-material/Refresh';
import { apiFetch } from '../helpers';

interface FmRow {
  _id: string;
  fullAddress: string;
  address: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  listingPrice: number | null;
  amv: number | null;
  bofaAmv: number | null;
  redfinAmv: number | null;
  realtorAmv: number | null;
  amvSource: string | null;
  amvError: string | null;
  isDeal: boolean;
  dealStage: string | null;
  scrapedAt: string;
  updatedAt: string;
}

interface StatusResponse {
  ok: boolean;
  isRunning: boolean;
  progress: {
    rangesCompleted: number;
    totalRanges: number;
    totalProperties: number;
    currentRange: string | null;
    cycle: number;
    lastMinP?: number | null;
    progressUpdatedAt?: string | null;
  };
  totalInDb: number;
  lastScrapedAt?: string | null;
}

interface Counts {
  total: number;
  withAmv: number;
  bofaQueue: number;
  redfinQueue: number;
  withoutAmv: number;
  deals: number;
}

type ViewMode = 'all' | 'with_amv' | 'bofa_queue' | 'redfin_queue' | 'without_amv';

const currency = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmt = (n?: number | null) => (typeof n === 'number' && n > 0 ? currency.format(n) : '—');

export default function FannieMaeFetcher() {
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [counts, setCounts] = useState<Counts>({ total: 0, withAmv: 0, bofaQueue: 0, redfinQueue: 0, withoutAmv: 0, deals: 0 });
  const [byState, setByState] = useState<{ _id: string; count: number }[]>([]);
  const [rows, setRows] = useState<FmRow[]>([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>('all');
  const [filterState, setFilterState] = useState('all');
  const [loading, setLoading] = useState(false);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [error, setError] = useState('');

  // BofA runner state
  const [bofaRunning, setBofaRunning] = useState(false);
  const [bofaStatus, setBofaStatus] = useState('');
  const bofaPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiFetch('/api/fanniemae-scrape/status');
      setStatus(await res.json());
    } catch (err: any) { setError(err.message); }
  }, []);

  const fetchCounts = useCallback(async () => {
    try {
      const res = await apiFetch('/api/fanniemae-scrape/compare');
      const data = await res.json();
      if (data.ok) {
        setCounts(data.counts);
        setByState(data.byState || []);
      }
    } catch (err: any) { setError(err.message); }
  }, []);

  const fetchRows = useCallback(async () => {
    setRowsLoading(true);
    try {
      const stateParam = filterState !== 'all' ? `&state=${filterState}` : '';
      const res = await apiFetch(`/api/fanniemae-scrape/properties?viewMode=${viewMode}&limit=200${stateParam}`);
      const data = await res.json();
      if (data.ok) {
        setRows(data.properties);
        setRowsTotal(data.total);
      }
    } catch (err: any) { setError(err.message); }
    finally { setRowsLoading(false); }
  }, [viewMode, filterState]);

  const fetchBofaStatus = useCallback(async () => {
    try {
      const res = await apiFetch('/api/fanniemae-scrape/bofa/status');
      const data = await res.json();
      if (data.success) {
        setBofaRunning(data.isProcessing || false);
        setBofaStatus(data.currentStatus || '');
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchCounts();
    fetchBofaStatus();
    const interval = setInterval(() => { fetchStatus(); fetchCounts(); fetchBofaStatus(); }, 5000);
    return () => clearInterval(interval);
  }, [fetchStatus, fetchCounts, fetchBofaStatus]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    if (bofaRunning && !bofaPollRef.current) {
      bofaPollRef.current = setInterval(fetchBofaStatus, 5000);
    } else if (!bofaRunning && bofaPollRef.current) {
      clearInterval(bofaPollRef.current);
      bofaPollRef.current = null;
    }
    return () => {
      if (bofaPollRef.current) {
        clearInterval(bofaPollRef.current);
        bofaPollRef.current = null;
      }
    };
  }, [bofaRunning, fetchBofaStatus]);

  const handleStart = async () => {
    setLoading(true); setError('');
    try {
      const res = await apiFetch('/api/fanniemae-scrape/start', { method: 'POST', body: JSON.stringify({}) });
      const data = await res.json();
      if (!data.ok) setError(data.error || 'Failed to start');
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleStop = async () => {
    try { await apiFetch('/api/fanniemae-scrape/stop', { method: 'POST' }); } catch (err: any) { setError(err.message); }
  };

  const handleStartBofa = async () => {
    try {
      const res = await apiFetch('/api/fanniemae-scrape/bofa/start', { method: 'POST' });
      const data = await res.json();
      if (data.success) { setBofaRunning(true); setBofaStatus('Starting...'); }
      else setError(data.message || 'Failed to start BofA');
    } catch (err: any) { setError(err.message); }
  };

  const handleStopBofa = async () => {
    try { await apiFetch('/api/fanniemae-scrape/bofa/stop', { method: 'POST' }); } catch { /* ignore */ }
  };

  const isRunning = status?.isRunning || false;
  const progress = status?.progress;
  const pct = progress && progress.totalRanges > 0 ? Math.round((progress.rangesCompleted / progress.totalRanges) * 100) : 0;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#111' }}>
          Fannie Mae Properties
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stat cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Total</Typography>
          <Typography variant="h5" fontWeight={700}>{counts.total.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">With AMV</Typography>
          <Typography variant="h5" fontWeight={700} color="success.main">{counts.withAmv.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">BofA Queue</Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#2563eb' }}>{counts.bofaQueue.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Redfin Queue</Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#dc2626' }}>{counts.redfinQueue.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Without AMV</Typography>
          <Typography variant="h5" fontWeight={700} color="warning.main">{counts.withoutAmv.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Deals</Typography>
          <Typography variant="h5" fontWeight={700} color="primary.main">{counts.deals.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 160 }}>
          <Typography variant="caption" color="text.secondary">Cycle</Typography>
          <Typography variant="h5" fontWeight={700}>#{progress?.cycle ?? 0}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {isRunning
              ? (progress?.currentRange ? `Running · ${progress.currentRange}` : 'Running')
              : status?.lastScrapedAt
                ? `Idle · last ${new Date(status.lastScrapedAt).toLocaleDateString()}`
                : 'Idle'
            }
          </Typography>
          {!isRunning && progress?.lastMinP != null && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 10 }}>
              Resume from ${progress.lastMinP}k
            </Typography>
          )}
        </Paper>
      </Box>

      {/* Controls */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          {!isRunning ? (
            <Button variant="contained" color="success" startIcon={<PlayArrowIcon />} onClick={handleStart} disabled={loading} sx={{ fontWeight: 600 }}>
              Start Scrape
            </Button>
          ) : (
            <Button variant="contained" color="error" startIcon={<StopIcon />} onClick={handleStop} sx={{ fontWeight: 600 }}>
              Stop
            </Button>
          )}
          {!bofaRunning ? (
            <Button variant="outlined" color="info" size="small" startIcon={<PlayArrowIcon />} onClick={handleStartBofa}>
              Start BofA
            </Button>
          ) : (
            <Button variant="outlined" color="error" size="small" startIcon={<StopIcon />} onClick={handleStopBofa}>
              Stop BofA
            </Button>
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { fetchStatus(); fetchCounts(); fetchRows(); }}>
            Refresh
          </Button>
          {bofaStatus && (
            <Typography variant="body2" color="text.secondary">
              {bofaRunning && <CircularProgress size={14} sx={{ mr: 1 }} />}
              {bofaStatus}
            </Typography>
          )}
        </Stack>

        {isRunning && progress && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>
                {progress.rangesCompleted}/{progress.totalRanges} ranges — {progress.totalProperties} saved (cycle #{progress.cycle})
              </Typography>
              {progress.currentRange && (
                <Chip label={`Scraping: ${progress.currentRange}`} color="info" size="small" />
              )}
            </Stack>
            <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3 }} />
          </Box>
        )}
      </Paper>

      {/* Tabs + state filter */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
        <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)} size="small">
          <ToggleButton value="all" sx={{ textTransform: 'none', fontWeight: 700 }}>
            All ({counts.total.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="with_amv" sx={{ textTransform: 'none', fontWeight: 600 }}>
            With AMV ({counts.withAmv.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="bofa_queue" sx={{ textTransform: 'none', fontWeight: 600 }}>
            BofA Queue ({counts.bofaQueue.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="redfin_queue" sx={{ textTransform: 'none', fontWeight: 600 }}>
            Redfin Queue ({counts.redfinQueue.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="without_amv" sx={{ textTransform: 'none', fontWeight: 600 }}>
            Without AMV ({counts.withoutAmv.toLocaleString()})
          </ToggleButton>
        </ToggleButtonGroup>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>State</InputLabel>
          <Select value={filterState} label="State" onChange={(e) => setFilterState(e.target.value)}>
            <MenuItem value="all">All States</MenuItem>
            {byState.map((s) => (
              <MenuItem key={s._id} value={s._id}>{s._id} ({s.count})</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Dark-header native table */}
      <div
        style={{
          overflowX: 'auto',
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          background: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, color: '#111' }}>
            {viewMode === 'all' && `All Properties (${counts.total.toLocaleString()})`}
            {viewMode === 'with_amv' && `With AMV (${counts.withAmv.toLocaleString()})`}
            {viewMode === 'bofa_queue' && `BofA Queue (${counts.bofaQueue.toLocaleString()})`}
            {viewMode === 'redfin_queue' && `Redfin Queue (${counts.redfinQueue.toLocaleString()})`}
            {viewMode === 'without_amv' && `Without AMV (${counts.withoutAmv.toLocaleString()})`}
          </span>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            Showing {rows.length}{rowsTotal > rows.length ? ` of ${rowsTotal.toLocaleString()}` : ''} rows
          </span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr style={{ background: '#111827', color: '#fff' }}>
              {['Address', 'State', 'List Price', 'AMV', 'Source', 'Deal?', 'Status'].map((h, i) => (
                <th
                  key={h}
                  style={{
                    textAlign: i === 0 ? 'left' : 'right',
                    padding: '12px 14px',
                    fontSize: 12,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    fontWeight: 600,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowsLoading && (
              <tr>
                <td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                  <CircularProgress size={18} sx={{ mr: 1 }} /> Loading...
                </td>
              </tr>
            )}
            {!rowsLoading && rows.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                  {viewMode === 'all' && 'No properties yet. Click "Start Scrape" to fetch from Fannie Mae.'}
                  {viewMode === 'with_amv' && 'No properties with AMV yet.'}
                  {viewMode === 'bofa_queue' && 'BofA queue is empty.'}
                  {viewMode === 'redfin_queue' && 'Redfin queue is empty — nothing waiting for Redfin fallback.'}
                  {viewMode === 'without_amv' && 'Nothing here — every property got an AMV from BofA or Redfin.'}
                </td>
              </tr>
            )}
            {!rowsLoading && rows.map((p, i) => {
              const zebra = i % 2 === 0 ? '#ffffff' : '#f9fafb';
              const bg = p.isDeal ? '#f0fdf4' : zebra;
              const amv = p.amv || p.bofaAmv || p.redfinAmv || p.realtorAmv || 0;
              const hasAmv = amv > 0;
              return (
                <tr key={p._id} style={{ background: bg }}>
                  <td style={{ padding: 14, minWidth: 280, color: '#111', fontWeight: 600 }}>
                    {p.fullAddress || '—'}
                  </td>
                  <td style={{ padding: 14, textAlign: 'right', color: '#6b7280' }}>{p.state || '—'}</td>
                  <td style={{ padding: 14, textAlign: 'right', color: '#059669', fontWeight: 600 }}>{fmt(p.listingPrice)}</td>
                  <td style={{ padding: 14, textAlign: 'right', color: hasAmv ? '#7c3aed' : '#9ca3af', fontWeight: 600 }}>
                    {hasAmv ? fmt(amv) : '—'}
                  </td>
                  <td style={{ padding: 14, textAlign: 'right' }}>
                    {p.amvSource ? (
                      <Chip
                        label={p.amvSource}
                        size="small"
                        sx={{
                          fontSize: 10,
                          fontWeight: 600,
                          bgcolor: p.amvSource === 'bofa' ? '#ede9fe' : p.amvSource === 'redfin' ? '#dcfce7' : '#dbeafe',
                          color: p.amvSource === 'bofa' ? '#6d28d9' : p.amvSource === 'redfin' ? '#166534' : '#1d4ed8',
                        }}
                      />
                    ) : (
                      <span style={{ color: '#9ca3af' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: 14, textAlign: 'right' }}>
                    {p.isDeal ? (
                      <Chip label="DEAL" size="small" sx={{ bgcolor: '#22c55e', color: '#fff', fontWeight: 600, fontSize: 10 }} />
                    ) : (
                      <span style={{ color: '#9ca3af' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: 14, textAlign: 'right' }}>
                    <Chip
                      label={p.amvError === 'bofa_no_data' ? 'no data' : p.amvError ? 'failed' : p.dealStage || 'pending'}
                      size="small"
                      title={p.amvError || undefined}
                      sx={{
                        fontSize: 10,
                        fontWeight: 600,
                        maxWidth: 140,
                        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                        bgcolor:
                          hasAmv ? '#dcfce7' :
                          p.amvError === 'bofa_no_data' ? '#fef3c7' :
                          p.amvError ? '#fee2e2' :
                          p.dealStage === 'bofa_amv_pending' ? '#dbeafe' : '#f3f4f6',
                        color:
                          hasAmv ? '#166534' :
                          p.amvError === 'bofa_no_data' ? '#92400e' :
                          p.amvError ? '#991b1b' :
                          p.dealStage === 'bofa_amv_pending' ? '#1d4ed8' : '#6b7280',
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Box>
  );
}
